import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { coursesAPI } from '../services/api';

const CourseCatalog = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await coursesAPI.getAll();
      setCourses(response.data.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto bg-background min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600 mx-auto"></div>
          <p className="mt-4 text-earth-500">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto bg-background min-h-screen">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={fetchCourses}
            className="mt-4 px-4 py-2 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto bg-background min-h-screen">
      <motion.h1
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-4xl font-serif font-bold mb-8 text-center gradient-text"
      >
        Explore Courses
      </motion.h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
        {courses.map((course, idx) => (
          <motion.div
            key={course.id}
            className="bg-card border border-earth-200 rounded-2xl overflow-hidden relative group"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.15, duration: 0.7, type: 'spring' }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={course.image_url}
                alt={course.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop
                  e.target.src = `https://via.placeholder.com/600x400/FF6B35/FFFFFF?text=${encodeURIComponent(course.title)}`;
                }}
              />
              <div className="absolute top-2 left-2 bg-saffron-400 text-xs px-3 py-1 rounded-full text-white font-semibold">
                {course.category}
              </div>
            </div>
            <div className="p-6">
              <h2 className="text-xl font-serif font-bold mb-2 text-earth-900">{course.title}</h2>
              <p className="text-earth-700 mb-4 min-h-[48px]">{course.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-saffron-600">
                  ₹{course.price}
                </span>
                <Link
                  to={`/course/${course.id}`}
                  className="px-4 py-2 rounded-lg border border-saffron-400 text-saffron-600 hover:bg-saffron-50 transition-colors text-sm font-medium"
                >
                  View Details
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default CourseCatalog; 